import React, { useState, useEffect } from 'react';
import { useField } from 'formik';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  Slider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

const Terminierung = ({ formikProps }) => {
  const { setFieldValue, values } = formikProps;
  const [openDatePickers, setOpenDatePickers] = useState({ 1: false, 2: false, 3: false });

  const [dringlichkeitDAYS] = useField('dringlichkeitDAYS');

  const futureDate = (() => {
    const calculatedDate = dayjs().add(dringlichkeitDAYS.value, 'day');
    const minimumDate = dayjs('2025-01-07');
    return calculatedDate.isBefore(minimumDate) ? minimumDate : calculatedDate;
  })();

  const isWeekend = (date) => {
    const day = dayjs(date).day();
    return day === 0 || day === 6;
  };

  const timeLabels = Array.from({ length: 41 }, (_, i) =>
    dayjs().startOf('day').add(8 * 60 + i * 15, 'minute').format('HH:mm')
  );

  const terminFields = [
    { label: 'Terminvorschlag 1', field: 'termin1', sliderField: 'termin1_slider', index: 1 },
    { label: 'Terminvorschlag 2', field: 'termin2', sliderField: 'termin2_slider', index: 2 },
    { label: 'Terminvorschlag 3', field: 'termin3', sliderField: 'termin3_slider', index: 3 },
  ];

  const parseTime = (str) => {
    const match = /^(\d{2}\.\d{2}\.\d{4}) - (\d{2}):(\d{2})/.exec(str || '');
    if (!match) return [null, null];
    const [, date, hour, minute] = match;
    return [date, parseInt(hour) * 60 + parseInt(minute)];
  };

  useEffect(() => {
    terminFields.forEach(({ sliderField }) => {
      if (!values[sliderField]) {
        const defaultMinutes = [8 * 60, 11 * 60]; // 08:00 bis 11:00
        setFieldValue(sliderField, defaultMinutes);
      }
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" mb={0}>Bitte wählen Sie 3 Terminvorschläge</Typography>
        <Typography variant="body1" pb={5}>Unser Handwerkspartner meldet sich bei Ihnen zur genauen Terminabstimmung.</Typography>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <Grid container spacing={2}>
          {terminFields.map(({ label, field, sliderField, index }) => {
            const [datePart, timeMinutes] = parseTime(values[field]);
            const currentSlider = values[sliderField] || [8 * 60, 10 * 60];

            const startIndex = (currentSlider[0] - 8 * 60) / 15;
            const endIndex = (currentSlider[1] - 8 * 60) / 15;

            return (
              <Grid item xs={12} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">📅 {label}</Typography>

                    <Box
                      sx={{ mt: 2, cursor: 'pointer' }}
                      onClick={() => setOpenDatePickers((prev) => ({ ...prev, [index]: true }))}
                    >
                      <TextField
                        label="Datum"
                        placeholder="Datum wählen"
                        value={datePart || ''}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon />
                            </InputAdornment>
                          ),
                          // Style the input element to show pointer
                          sx: { '& .MuiInputBase-input': { cursor: 'pointer' } }
                        }}
                      />
                    </Box>

                    <Dialog
                      open={openDatePickers[index]}
                      onClose={() => setOpenDatePickers((prev) => ({ ...prev, [index]: false }))}
                    >
                      <Box sx={{ p: 2 }}>
                        <DateCalendar
                          value={datePart ? dayjs(datePart, 'DD.MM.YYYY') : null}
                          onChange={(newDate) => {
                            const startTime = dayjs()
                              .startOf('day')
                              .add(currentSlider[0], 'minute')
                              .format('HH:mm');
                            const value = dayjs(newDate).format('DD.MM.YYYY') + ' - ' + startTime + ':00';
                            setFieldValue(field, value);
                            setOpenDatePickers((prev) => ({ ...prev, [index]: false }));
                          }}
                          shouldDisableDate={isWeekend}
                          minDate={futureDate}
                        />
                      </Box>
                    </Dialog>

                    <Box sx={{ mt: 4, px: 2, overflow: 'visible' }}>
                      <Slider
                        value={[startIndex, endIndex]}
                        onChange={(_, newValue) => {
                          const [start, end] = newValue;
                          const selectedDate = datePart || '';
                          const startTime = timeLabels[start];
                          const endTime = timeLabels[end];

                          const startMinutes = 8 * 60 + start * 15;
                          const endMinutes = 8 * 60 + end * 15;

                          setFieldValue(sliderField, [startMinutes, endMinutes]);
                          if (selectedDate) {
                            setFieldValue(field, `${selectedDate} - ${startTime}:00`);
                          }
                        }}
                        step={1}
                        marks
                        min={0}
                        max={timeLabels.length - 1}
                        valueLabelDisplay="on"
                        valueLabelFormat={(val) => timeLabels[val]}
                      />
                      <Typography variant="body2" align="center" mt={1}>
                        Gewählter Bereich: {timeLabels[startIndex]} – {timeLabels[endIndex]}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </LocalizationProvider>
    </Box>
  );
};

export default Terminierung;
